import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { BrandMark } from "@/brand/BrandMark";
import { usePreReservasSite, PRE_RESERVAS_SITE_URL } from "@/hooks/usePreReservasSite";

const RESERVAS_TESTID = "nav-reservas";

function PreReservaBadge({ count, testid, className = "" }) {
  const navigate = useNavigate();
  if (!count) return null;
  return (
    <span role="link" tabIndex={0} data-testid={testid}
      title={`${count} pré-reserva(s) do site aguardando`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(PRE_RESERVAS_SITE_URL); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); navigate(PRE_RESERVAS_SITE_URL); } }}
      className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#D4A652] text-white text-[10px] font-bold tabular-nums cursor-pointer hover:bg-[#B88B3A] transition-colors ${className}`}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function Sidebar({ user, onLogout, navItems }) {
  const location = useLocation();
  const preCount = usePreReservasSite(navItems.some((n) => n.testid === RESERVAS_TESTID));
  return (
    <aside data-testid="sidebar" className="hidden lg:flex lg:w-72 flex-col border-r border-stone-200 bg-[#FFFFFF] min-h-screen sticky top-0">
      <div className="p-8 border-b border-stone-200">
        <div className="flex items-center gap-3">
          <BrandMark variant="mark" size={40} />
          <div>
            <div className="font-brand-title text-xl text-[#083A6B] font-bold leading-none">Rede Hospeda</div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1.5">Gestão de Pousadas</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-400 px-3 py-2">Navegação</div>
        {navItems.map((n) => {
          const Icon = n.icon;
          const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} data-active={active}
              className="nav-link flex items-center gap-3 px-4 py-3 rounded-md text-stone-700 hover:bg-stone-100 text-sm font-medium">
              <Icon size={18} strokeWidth={1.6} />
              <span className="flex-1">{n.label}</span>
              {n.testid === RESERVAS_TESTID && <PreReservaBadge count={preCount} testid="sidebar-pre-reservas-count" />}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-6 border-t border-stone-200 space-y-4">
        {user && (
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#EAF0EA] flex items-center justify-center text-[#083A6B] text-xs font-semibold">
                {(user.name || user.email || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-stone-800 truncate">{user.name || user.email}</div>
              <div className="text-[10px] text-stone-500 truncate">{user.email}</div>
              {user.is_accountant && !user.is_admin && !user.is_finance && (
                <div data-testid="badge-perfil-contador"
                  className="mt-1 inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-semibold bg-[#8B6A1F]/15 text-[#5c460f] border border-[#8B6A1F]/30">
                  Contador · somente leitura
                </div>
              )}
            </div>
          </div>
        )}
        <button data-testid="btn-logout" onClick={onLogout}
          className="flex items-center gap-2 text-xs text-stone-600 hover:text-[#D4A652] transition-colors">
          <LogOut size={14} /> Sair
        </button>
      </div>
    </aside>
  );
}

export function MobileHeader({ onLogout }) {
  return (
    <header className="lg:hidden border-b border-stone-200 bg-[#FFFFFF] px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <BrandMark variant="mark" size={32} />
        <div className="font-brand-title text-base text-[#083A6B] font-bold leading-none">Rede Hospeda</div>
      </div>
      <button onClick={onLogout} className="text-stone-500 hover:text-[#D4A652]" aria-label="Sair" data-testid="btn-logout-mobile">
        <LogOut size={18} />
      </button>
    </header>
  );
}

export function MobileTabs({ navItems }) {
  const location = useLocation();
  const items = navItems.filter((n) => n.mobile);
  const preCount = usePreReservasSite(items.some((n) => n.testid === RESERVAS_TESTID));
  // F-FIX.PACOTE.5 · Parte 2 — mais de 5 itens: scroll horizontal controlado,
  // mantendo toque confortável e sem overflow da página.
  return (
    <nav
      data-testid="mobile-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FFFFFF] border-t border-stone-200 flex overflow-x-auto no-scrollbar"
    >
      {items.map((n) => {
        const Icon = n.icon;
        const active = n.end ? location.pathname === n.to : location.pathname.startsWith(n.to);
        return (
          <NavLink key={n.to} to={n.to} end={n.end}
            className={`relative shrink-0 min-w-[64px] flex-1 flex flex-col items-center gap-1 py-2 px-1 text-[10px] ${active ? "text-[#083A6B] font-semibold" : "text-stone-500"}`}>
            {n.testid === RESERVAS_TESTID && <PreReservaBadge count={preCount} testid="mobile-pre-reservas-count" className="absolute top-0.5 right-2" />}
            <Icon size={18} strokeWidth={1.6} />
            <span className="truncate max-w-[70px]">{n.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
